import type { ReconciliationSpecCreateOperation } from '@festina/configuration-general-types/configuration'
import type { Except } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { useMutateConvertExcelToReconciliationSpecCreateOperation } from '../../uiModels/api/reconciliationSpecs/useMutateConvertExcelToReconciliationSpecCreateOperation'
import type { DraftCreationProps } from '../draftCreationModal/types'
import { ExcelUploadForm } from '../excelUploadForm/ExcelUploadForm'

type Props = Except<
  DraftCreationProps<'reconciliationSpec'>,
  'initialValue'
> & {
  formId: string
  expectedSpecName?: string
}

/**
 * Creates a reconciliation spec, or a new version of one, out of a reconciliation-spec
 * Excel document.
 */
export const ReconciliationSpecExcelUploadForm = ({
  formId,
  layer,
  block,
  expectedSpecName,
  onStatusChanged,
}: Props) => {
  const translate = useTranslate()
  const { mutateAsync } =
    useMutateConvertExcelToReconciliationSpecCreateOperation()

  return (
    <ExcelUploadForm
      formId={formId}
      layer={layer}
      block={block}
      expectedEntityName={expectedSpecName}
      excelConfig={{
        getEntityId: (operation: ReconciliationSpecCreateOperation) => ({
          type: 'reconciliationSpec',
          layer,
          block,
          name: operation.reconciliationSpec?.specId ?? '',
        }),
        convertToOperation: (excelFile) => mutateAsync({ excelFile }),
        missingEntityNameError: translate(
          'reconciliationSpecDraftForm.missingSpecName',
        ),
        getEntityNameMismatchError: (specName, expectedSpecName) =>
          translate('reconciliationSpecDraftForm.specNameMismatch', {
            specName,
            expectedSpecName,
          }),
        getUploadErrorMessage: (error) =>
          translate('reconciliationSpecDraftForm.error', { error }),
      }}
      onStatusChanged={onStatusChanged}
    />
  )
}
